// ** Types
import type { NextRouter } from 'next/router';
import type { StrapiPageEntity } from '@/types/strapi';
import type { NavGroup, NavLink } from '@/types/layoutProps';

/**
 * Builds a path from a Strapi relation link object.
 * Accepts undefined to match typical GraphQL/Optional types.
 */
export const buildPathFromRelationLink = (pageLink?: StrapiPageEntity | null): string => {
	if (!pageLink) return '/';

	const segments: string[] = [];
	let current: StrapiPageEntity = pageLink;

	while (current) {
		if (current.slug) {
			segments.unshift(current.slug);
		}
		current = current.parent_page;
	}

	const path = segments.join('/');
	return path ? `/${path}` : '/';
};

/**
 * Recursively checks whether a given navigation group (or any of its nested children)
 * has an active link matching the current URL.
 *
 * @param item The navigation group or link item to evaluate.
 * @param currentURL The current route (typically `router.asPath`).
 */
export const hasActiveChild = (item: NavGroup | NavLink, currentURL: string): boolean => {
	const matchPath = (path?: string) => {
		if (!path) return false;
		return currentURL === path || (currentURL.startsWith(path) && path !== '/');
	};

	// Check if this item directly matches the current path
	const path =
		'url' in item && item.url ? item.url : item.pageLink ? buildPathFromRelationLink(item.pageLink) : undefined;

	if (matchPath(path as string | undefined)) return true;

	// If item has children, recursively check them
	if ('children' in item && Array.isArray(item.children)) {
		return item.children.some((child) => hasActiveChild(child, currentURL));
	}

	return false;
};

/**
 * Check if this is a children
 * of the given item
 *
 * @param children
 * @param openGroup
 * @param currentActiveGroup
 */
export const removeChildren = (children: NavLink[], openGroup: string[], currentActiveGroup: string[]) => {
	children.forEach((child: NavLink) => {
		if (!currentActiveGroup.includes(child.title)) {
			const index = openGroup.indexOf(child.title);
			if (index > -1) openGroup.splice(index, 1);

			if (child.children) removeChildren(child.children, openGroup, currentActiveGroup);
		}
	});
};

/**
 * Check for URL queries as well for matching
 * Current URL & Item Path
 *
 * @param item
 * @param activeItem
 */
export const handleURLQueries = (router: NextRouter, path: string | undefined): boolean => {
	if (!path) return false; // Ensure path is provided

	// Special case for `/user/profile/my-volunteering` when it has `theme` in the query
	if (path === '/user/profile/my-volunteering' && 'theme' in router.query) {
		return true;
	}

	// Prevent false positive for `/user/profile` when path is `/user/profile/my-volunteering`
	if (path === '/user/profile' && 'theme' in router.query) {
		return false; // Ensure this does not return true for `/user/profile`
	}

	// Handle other cases with dynamic path matching
	if (Object.keys(router.query).length && path) {
		const queryKey = Object.keys(router.query)[0]; // First query key (for dynamic paths)
		const dynamicPath = router.query[queryKey] as string;

		// General condition for matching path and dynamic path segment
		return router.asPath.includes(path) && router.asPath.includes(dynamicPath) && path !== '/';
	}

	return false;
};
